import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { api } from '../utils/api';
import { Building2, Upload, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

export const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    cif_nif: '',
    address: '',
    phone: '',
    company_email: '',
    website: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const response = await api.getMyCompanyData();
      const data = response.data;
      
      setFormData({
        company_name: data.company_name || '',
        cif_nif: data.cif_nif || '',
        address: data.address || '',
        phone: data.phone || '',
        company_email: data.company_email || '',
        website: data.website || ''
      });
      
      if (data.logo_base64) {
        setLogoPreview(data.logo_base64);
      }
    } catch (error) {
      toast.error('Error al cargar datos de empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setLogoFile(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Estás seguro de eliminar el logo?')) return;

    try {
      await api.deleteCompanyLogo();
      setLogoPreview(null);
      setLogoFile(null);
      toast.success('Logo eliminado correctamente');
    } catch (error) {
      toast.error('Error al eliminar el logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Actualizar datos de empresa
      await api.updateMyCompanyData(formData);
      
      // Si hay un nuevo logo, subirlo
      if (logoFile && logoFile !== logoPreview) {
        await api.uploadCompanyLogo(logoFile);
      }
      
      toast.success('Datos de empresa actualizados correctamente');
      fetchCompanyData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al guardar datos de empresa';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos de empresa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Configuración de Empresa</h1>
        </div>
        <p className="text-gray-600">
          Configura los datos fiscales y el logo de tu empresa. Esta información aparecerá en los albaranes en formato PDF.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Section */}
        <Card className="!bg-white dark:!bg-slate-900 !border-gray-200 dark:!border-slate-700">
          <CardHeader className="!bg-white dark:!bg-slate-900">
            <CardTitle>Logo de Empresa</CardTitle>
            <CardDescription>
              Sube el logo de tu empresa (máx. 2MB, formatos: JPG, PNG, SVG)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {logoPreview ? (
                <div className="relative">
                  <img 
                    src={logoPreview} 
                    alt="Logo preview" 
                    className="h-32 w-32 object-contain border-2 border-gray-200 rounded-lg p-2"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2"
                    onClick={handleDeleteLogo}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="h-32 w-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="h-8 w-8 text-gray-400" />
                </div>
              )}
              
              <div className="flex-1">
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      Haz clic aquí o arrastra tu logo
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PNG, JPG o SVG (máx. 2MB)
                    </p>
                  </div>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Data Section */}
        <Card className="!bg-white dark:!bg-slate-900 !border-gray-200 dark:!border-slate-700">
          <CardHeader className="!bg-white dark:!bg-slate-900">
            <CardTitle>Datos Fiscales</CardTitle>
            <CardDescription>
              Información de tu empresa que aparecerá en los documentos oficiales
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Nombre Comercial *</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  placeholder="Mi Empresa S.L."
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="cif_nif">CIF / NIF *</Label>
                <Input
                  id="cif_nif"
                  name="cif_nif"
                  placeholder="B12345678"
                  value={formData.cif_nif}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Dirección Completa *</Label>
              <Input
                id="address"
                name="address"
                placeholder="Calle Principal 123, 28001 Madrid, España"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Teléfono *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+34 912 345 678"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="company_email">Email de Empresa *</Label>
                <Input
                  id="company_email"
                  name="company_email"
                  type="email"
                  placeholder="info@miempresa.com"
                  value={formData.company_email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="website">Sitio Web</Label>
              <Input
                id="website"
                name="website"
                type="url"
                placeholder="https://www.miempresa.com"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => fetchCompanyData()}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Info Box */}
      <Card className="mt-6 !bg-blue-50 dark:!bg-slate-900 !border-blue-200 dark:!border-blue-800">
        <CardContent className="pt-6 !bg-blue-50 dark:!bg-slate-900">
          <div className="flex gap-3">
            <div className="text-blue-600 mt-1">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                ℹ️ Información Importante
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Estos datos aparecerán automáticamente en todos tus albaranes en PDF</li>
                <li>• El logo se mostrará en la esquina superior izquierda del documento</li>
                <li>• Puedes actualizar esta información en cualquier momento</li>
                <li>• Los campos marcados con * son obligatorios para generar PDFs profesionales</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
